import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { portLabel, portMatches, portStarts } from '../data/portAliases'
import { SEED_DESTINATIONS, SEED_ORIGINS } from '../data/seedPorts'
import { useLocalePath, useT } from '../i18n'
import { flagFor, getBox, setBox } from '../lib/personal'
import { type LaneRow, cleanPort, laneSlug } from '../lib/rates'

interface Port {
  code: string
  name: string
  country: string | null
}

// Build a clean, ranked port list: dedupe by code, keep only ports with a real human name (drop
// code-only junk like "CNAQG"), and sort by how many lanes touch the port so majors surface first.
function buildPorts(lanes: LaneRow[], role: 'pol' | 'pod'): Port[] {
  const m = new Map<string, Port & { count: number }>()
  for (const l of lanes) {
    const code = role === 'pol' ? l.pol_code : l.pod_code
    if (!code) continue
    const name = role === 'pol' ? l.pol_name : l.pod_name
    const country = role === 'pol' ? l.pol_country : l.pod_country
    const e = m.get(code)
    if (e) {
      e.count++
      if ((!e.name || e.name.toUpperCase() === code.toUpperCase()) && name) e.name = name
    } else {
      m.set(code, { code, name: name || code, country, count: 1 })
    }
  }
  return [...m.values()]
    .filter((p) => p.name && p.name.toUpperCase() !== p.code.toUpperCase())
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .map(({ code, name, country }) => ({ code, name, country }))
}

// Clean display name (strips the polluted ",Xx" suffix) — used for the field value, dropdown,
// popular chips and the geo nudge, so "Hamburg,De" never shows.
const cap = (s: string) => cleanPort(s)

/** A single typeahead port field with keyboard nav — the building block of the lane search. */
function PortField({
  label,
  ports,
  fallbackPorts,
  value,
  onChange,
  onCommit,
  placeholder,
}: {
  label: string
  ports: Port[]
  fallbackPorts?: Port[]
  value: string | null
  onChange: (code: string | null) => void
  onCommit?: () => void
  placeholder: string
}) {
  const selected = value
    ? (ports.find((p) => p.code === value) ?? fallbackPorts?.find((p) => p.code === value) ?? null)
    : null
  const [q, setQ] = useState(
    selected?.name ? portLabel(selected.code, selected.name, (n) => cap(n.toLowerCase())) : ''
  )
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const boxRef = useRef<HTMLDivElement>(null)

  // reflect external changes (swap, clear)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    () =>
      setQ(
        selected?.name ? portLabel(selected.code, selected.name, (n) => cap(n.toLowerCase())) : ''
      ),
    [value]
  )

  const query = q.trim().toLowerCase()
  const matches = useMemo(() => {
    if (!query) return ports.slice(0, 7)
    const match = (p: Port) => portMatches(p.code, p.name, query)
    // Names that START with the query lead (typing "Chit" puts Chittagong first), then the rest.
    const starts = (p: Port) => (portStarts(p.code, p.name, query) ? 0 : 1)
    const primary = ports
      .filter(match)
      .map((p, i) => ({ p, i }))
      .sort((a, b) => starts(a.p) - starts(b.p) || a.i - b.i)
      .map(({ p }) => p)
    // No lane from this origin matches — offer any port; the pair routes to a quote scenario.
    if (primary.length === 0 && fallbackPorts) return fallbackPorts.filter(match).slice(0, 7)
    return primary.slice(0, 7)
  }, [query, ports, fallbackPorts])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  function choose(p: Port) {
    onChange(p.code)
    setQ(portLabel(p.code, p.name, (n) => cap(n.toLowerCase())))
    setOpen(false)
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActive((a) => Math.min(a + 1, matches.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(a - 1, 0))
    } else if (e.key === 'Enter') {
      if (open && matches[active]) {
        e.preventDefault()
        choose(matches[active])
      } else if (value) {
        onCommit?.()
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div ref={boxRef} className="relative min-w-0 flex-1">
      <div className="flex items-center px-4 py-2.5">
        {selected && (
          <span className="mr-2.5 shrink-0 text-lg leading-none" aria-hidden="true">
            {flagFor(selected.code)}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="mono text-[10px] uppercase tracking-wider text-faint">{label}</div>
          <input
            value={q}
            placeholder={placeholder}
            spellCheck={false}
            autoComplete="off"
            onFocus={() => {
              setOpen(true)
              setActive(0)
            }}
            onChange={(e) => {
              setQ(e.target.value)
              onChange(null)
              setOpen(true)
              setActive(0)
            }}
            onKeyDown={onKey}
            className="w-full bg-transparent text-[15px] font-semibold text-ink outline-none placeholder:font-normal placeholder:text-faint"
          />
        </div>
        {selected && (
          <span className="mono shrink-0 rounded-md bg-surface-1 px-1.5 py-0.5 text-[10px] font-semibold text-muted">
            {selected.code}
          </span>
        )}
      </div>

      {open && matches.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-50 mt-2 max-h-72 overflow-auto rounded-xl border border-border-strong bg-surface-0 py-1.5 card-shadow">
          {matches.map((p, i) => (
            <li key={p.code}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault()
                  choose(p)
                }}
                onMouseEnter={() => setActive(i)}
                className={`flex w-full items-center gap-3 px-3.5 py-2 text-left ${
                  i === active ? 'bg-surface-1' : ''
                }`}
              >
                <span className="shrink-0 text-lg leading-none" aria-hidden="true">
                  {flagFor(p.code)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-ink">
                    {portLabel(p.code, p.name, (n) => cap(n.toLowerCase()))}
                  </span>
                  {p.country && (
                    <span className="block truncate text-xs text-faint">
                      {cap(p.country.toLowerCase())}
                    </span>
                  )}
                </span>
                <span className="mono shrink-0 text-[10px] text-faint">{p.code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const CONTAINERS: { key: string; label: string }[] = [
  { key: '20gp', label: '20ft' },
  { key: '40gp', label: '40ft' },
  { key: '40hc', label: '40HC' },
]

/** Compact inline container picker — keeps the whole search on one precision line. */
function ContainerSelect({
  value,
  onChange,
  label,
}: { value: string; onChange: (k: string) => void; label: string }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])
  const current = CONTAINERS.find((c) => c.key === value) ?? CONTAINERS[2]
  return (
    <div ref={ref} className="relative flex items-center">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        className="flex items-center gap-1.5 rounded-xl px-3.5 py-2.5 text-[15px] font-semibold text-ink transition-colors hover:bg-surface-1"
      >
        <span className="mono">{current?.label}</span>
        <svg
          width="11"
          height="11"
          viewBox="0 0 12 12"
          aria-hidden="true"
          className={`text-faint transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <ul className="absolute right-0 top-full z-50 mt-2 min-w-[112px] rounded-xl border border-border-strong bg-surface-0 p-1 card-shadow">
          {CONTAINERS.map((c) => (
            <li key={c.key}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault()
                  onChange(c.key)
                  setOpen(false)
                }}
                className={`flex w-full items-center justify-between gap-3 rounded-md px-3 py-1.5 text-left ${
                  c.key === value ? 'text-signal' : 'text-ink hover:bg-surface-1'
                }`}
              >
                <span className="mono text-sm font-semibold">{c.label}</span>
                {c.key === value && (
                  <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Visitor's ISO region (GB, US, DE…) from the browser locale — no network, privacy-preserving. */
function visitorRegion(): string | null {
  if (typeof navigator === 'undefined') return null
  const tags = [navigator.language, ...(navigator.languages ?? [])]
  for (const t of tags) {
    try {
      const r = new Intl.Locale(t).region
      if (r) return r.toUpperCase()
    } catch {
      const m = /[-_]([A-Za-z]{2})\b/.exec(t)
      if (m?.[1]) return m[1].toUpperCase()
    }
  }
  return null
}

/** Hero lane search — From → To over the real port graph, so every search hits a lane with data. */
export function LaneSearch({
  lanes,
  variant = 'hero',
}: { lanes: LaneRow[]; variant?: 'hero' | 'compact' | 'bar' }) {
  const navigate = useNavigate()
  const t = useT()
  const lp = useLocalePath()
  const [from, setFrom] = useState<string | null>(null)
  const [to, setTo] = useState<string | null>(null)
  // Container box is remembered across visits so every rate the visitor sees is in their box.
  const [ctr, setCtr] = useState<string>(getBox)
  const changeCtr = (b: string) => {
    setCtr(b)
    setBox(b)
  }
  const compact = variant === 'compact'
  const heroExtras = variant === 'hero' // popular / geo-nudge / no-rate hint only on the full hero

  // The lane feed is the port graph. When it is slow or down, the fields used to sit on
  // "Loading ports…" with no suggestions and no way to type a lane by hand, so one failed
  // Supabase read took the whole search down (customer report, 2026-08-17). After a short
  // grace period we stop waiting and fall back to the seed port list: every pair still
  // resolves, to a lane page or to a quote.
  const [stalled, setStalled] = useState(false)
  useEffect(() => {
    if (lanes.length > 0) {
      setStalled(false)
      return
    }
    const id = window.setTimeout(() => setStalled(true), 6_000)
    return () => window.clearTimeout(id)
  }, [lanes.length])

  const liveOrigins = useMemo(() => buildPorts(lanes, 'pol'), [lanes])
  const liveDests = useMemo(() => buildPorts(lanes, 'pod'), [lanes])
  // Live ports first (ranked by lane count), then any seed port the feed does not carry. Once the
  // feed has loaded the seed is still merged in: a port with no published lane (Chittagong, Abu
  // Dhabi, Jebel Ali as an origin) must stay findable and route to a quote, not vanish from the list.
  const withSeed = (live: Port[], seed: Port[]) => {
    const have = new Set(live.map((p) => p.code))
    return [...live, ...seed.filter((p) => !have.has(p.code))]
  }
  const origins =
    liveOrigins.length > 0 || stalled ? withSeed(liveOrigins, SEED_ORIGINS) : liveOrigins
  const allDests =
    liveDests.length > 0 || stalled ? withSeed(liveDests, SEED_DESTINATIONS) : liveDests
  const destByOrigin = useMemo(() => {
    const m = new Map<string, Set<string>>()
    for (const l of lanes) {
      if (!l.pol_code || !l.pod_code) continue
      if (!m.has(l.pol_code)) m.set(l.pol_code, new Set())
      m.get(l.pol_code)?.add(l.pod_code)
    }
    return m
  }, [lanes])

  const dests = useMemo(() => {
    if (!from) return allDests
    const onLane = allDests.filter((p) => destByOrigin.get(from)?.has(p.code))
    // No live lane graph (seed fallback, or an origin we publish nothing from yet) — offer every
    // port rather than an empty dropdown; the pair routes to a quote.
    return onLane.length > 0 ? onLane : allDests
  }, [from, allDests, destByOrigin])

  // "Near you" nudge — the best-ranked lane whose destination is in the visitor's country.
  const nearby = useMemo(() => {
    const region = visitorRegion()
    if (!region) return null
    return lanes.find((l) => l.pod_code?.slice(0, 2).toUpperCase() === region) ?? null
  }, [lanes])

  const ready = Boolean(from && to)
  // A chosen pair we have no published lane for still resolves — to a quote scenario, not a dead end.
  const noLiveRate = Boolean(from && to && !destByOrigin.get(from)?.has(to))
  function go(f = from, dest = to) {
    if (f && dest) navigate(lp(`/rates/${laneSlug(f, dest)}?c=${ctr}`))
  }
  function swap() {
    // only swap when the reverse lane exists, else just move origin→dest
    setFrom(to)
    setTo(destByOrigin.get(to ?? '')?.has(from ?? '') ? from : null)
  }

  // "Loading" is only true while we are genuinely still waiting. Once the grace period lapses the
  // fields go live against the seed ports, so the placeholder must stop saying "Loading ports…".
  const loading = lanes.length === 0 && !stalled
  const popular = lanes.slice(0, 5)

  return (
    <div>
      <div
        className={`card flex flex-col items-stretch gap-1 rounded-2xl p-1.5 sm:flex-row sm:items-center ${
          compact ? 'shadow-none' : ''
        }`}
      >
        <div className="flex flex-1 flex-col sm:flex-row sm:items-center">
          <PortField
            label={t('search.from')}
            ports={origins}
            value={from}
            onChange={setFrom}
            onCommit={() => go()}
            placeholder={loading ? t('search.loadingPorts') : t('search.originPh')}
          />
          <button
            type="button"
            onClick={swap}
            disabled={!from && !to}
            aria-label={t('search.swap')}
            className="group mx-auto my-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-surface-0 text-muted transition-colors hover:border-signal hover:text-signal disabled:opacity-40 sm:my-0"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="transition-transform duration-300 group-hover:rotate-180"
            >
              <path d="M7 4v13M7 4L4 7M7 4l3 3" />
              <path d="M17 20V7M17 20l3-3M17 20l-3-3" />
            </svg>
          </button>
          <div className="hidden w-px self-stretch bg-border sm:block" />
          <PortField
            label={t('search.to')}
            ports={dests}
            fallbackPorts={allDests}
            value={to}
            onChange={setTo}
            onCommit={() => go()}
            placeholder={
              loading ? t('search.loadingPorts') : from ? t('search.destPh') : t('search.anyDest')
            }
          />
        </div>
        {!compact && (
          <>
            <div className="hidden w-px self-stretch bg-border sm:block" />
            <ContainerSelect value={ctr} onChange={changeCtr} label={t('search.container')} />
          </>
        )}
        <button
          type="button"
          onClick={() => go()}
          disabled={!ready}
          className="btn-accent shrink-0 rounded-xl px-6 py-3 text-center font-semibold disabled:cursor-not-allowed disabled:opacity-45"
        >
          {noLiveRate ? t('search.getQuote') : t('search.check')}
        </button>
      </div>

      {heroExtras && noLiveRate && <p className="mt-2 text-xs text-muted">{t('search.noRate')}</p>}

      {heroExtras && !loading && nearby && (
        <button
          type="button"
          onClick={() => go(nearby.pol_code, nearby.pod_code)}
          className="mt-3 inline-flex items-center gap-2 rounded-full border border-signal/30 bg-signal-soft px-3 py-1.5 text-xs font-medium text-signal transition-colors hover:bg-signal hover:text-on-accent"
        >
          <span aria-hidden="true">📍</span>
          {t('search.shippingTo')}{' '}
          {cap((nearby.pod_country || nearby.pod_name || nearby.pod_code).toLowerCase())}:{' '}
          {portLabel(nearby.pol_code, nearby.pol_name || nearby.pol_code, (n) =>
            cap(n.toLowerCase())
          )}{' '}
          →{' '}
          {portLabel(nearby.pod_code, nearby.pod_name || nearby.pod_code, (n) =>
            cap(n.toLowerCase())
          )}
        </button>
      )}

      {heroExtras && !loading && popular.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="mono text-[11px] uppercase tracking-wider text-faint">
            {t('search.popular')}
          </span>
          {popular.map((l) => (
            <button
              key={`${l.pol_code}-${l.pod_code}`}
              type="button"
              onClick={() => go(l.pol_code, l.pod_code)}
              className="rounded-full border border-border bg-surface-0 px-3 py-1 text-xs text-muted transition-colors hover:border-signal hover:text-ink"
            >
              <span aria-hidden="true">{flagFor(l.pol_code)}</span>{' '}
              {portLabel(l.pol_code, l.pol_name || l.pol_code, (n) => cap(n.toLowerCase()))} →{' '}
              <span aria-hidden="true">{flagFor(l.pod_code)}</span>{' '}
              {portLabel(l.pod_code, l.pod_name || l.pod_code, (n) => cap(n.toLowerCase()))}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
